import {
    Stack,
    Loader,
    Alert,
    Group,
    ScrollArea,
    UnstyledButton,
    Box,
    Select,
} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useTranslation} from "react-i18next";
import {Link, Outlet, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {TopChatSplashView} from "../chat/components/TopChatSplashView";
import type {PortalGrouping} from "../../dibAdminApiCodegen/dibadminApi";
import {usePortalData} from "./usePortalData";
import IconHome from "../../assets/home.svg?react";
import IconEdit from "../../assets/edit.svg?react";
import IconHistory from "../../assets/history.svg?react";
import IconChevronRight from "../../assets/chevron-right.svg?react";
import IconChevronDown from "../../assets/chevron-down.svg?react";
import IconX from "../../assets/x.svg?react";
import IconMessage2 from "../../assets/message-2.svg?react";
import classes from "./DashboardView.module.css";

type ActiveView = "portals" | "myWork" | "history" | "chatSearch";

function deriveActiveView(pathname: string): ActiveView {
    if (pathname === "/my-work") return "myWork";
    if (pathname === "/history") return "history";
    if (pathname === "/chat-search") return "chatSearch";
    return "portals";
}

export function DashboardLayout() {
    const {t} = useTranslation();
    const isMobile = useMediaQuery("(max-width: 48em)");
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const activeView = deriveActiveView(location.pathname);
    const selectedGroupingName = searchParams.get("filter");

    const {relevantGroupings, isLoading, isError} = usePortalData();

    if (isLoading) {
        return (
            <Stack p="md" align="center">
                <Loader />
            </Stack>
        );
    }

    if (isError) {
        return (
            <Stack p="md">
                <Alert color="red" title="Error">
                    Failed to load dashboard data.
                </Alert>
            </Stack>
        );
    }

    const isAllAreasActive =
        activeView === "portals" && selectedGroupingName === null;
    const groupingSelectData = [
        {
            value: "all",
            label: t("Widget.internal.portal.allAreas"),
        },
        ...relevantGroupings
            .filter(
                (g): g is PortalGrouping & {name: string} =>
                    g.name !== undefined
            )
            .map((g) => ({
                value: g.name,
                label: g.name,
            })),
    ];
    const mobileGroupingValue =
        activeView === "portals" && selectedGroupingName !== null
            ? selectedGroupingName
            : "all";

    return (
        <Stack align="center" w={"100%"} c={"dark.7"} className={classes.root}>
            <TopChatSplashView />
            {isMobile && (
                <Stack gap="sm" className={classes.mobileControls}>
                    <Group
                        wrap="nowrap"
                        align="stretch"
                        className={classes.mobileTabs}
                    >
                        <UnstyledButton
                            component={Link}
                            to="/"
                            className={classes.mobileAction}
                            data-active={activeView === "portals"}
                        >
                            <IconHome
                                className={classes.mobileActionIcon}
                            />
                            {t("Widget.internal.portal.content")}
                        </UnstyledButton>
                        <UnstyledButton
                            component={Link}
                            to="/my-work"
                            className={classes.mobileAction}
                            data-active={activeView === "myWork"}
                        >
                            <IconEdit
                                className={classes.mobileActionIcon}
                            />
                            {t("Widget.internal.portal.myWork")}
                        </UnstyledButton>
                        <UnstyledButton
                            component={Link}
                            to="/history"
                            className={classes.mobileAction}
                            data-active={activeView === "history"}
                        >
                            <IconHistory
                                className={classes.mobileActionIcon}
                            />
                            {t("Widget.internal.portal.history")}
                        </UnstyledButton>
                        <UnstyledButton
                            component={Link}
                            to="/chat-search"
                            className={classes.mobileAction}
                            data-active={activeView === "chatSearch"}
                        >
                            <IconMessage2
                                className={classes.mobileActionIcon}
                            />
                            {t("common.recentThreads")}
                        </UnstyledButton>
                    </Group>
                    {activeView === "portals" && (
                        <Select
                            value={mobileGroupingValue}
                            data={groupingSelectData}
                            allowDeselect={false}
                            className={classes.mobileSelect}
                            classNames={{
                                input: classes.mobileSelectInput,
                                dropdown: classes.mobileSelectDropdown,
                                option: classes.mobileSelectOption,
                            }}
                            rightSection={
                                <IconChevronDown
                                    height={18}
                                    width={18}
                                />
                            }
                            onChange={(value) => {
                                if (value === "all" || value === null) {
                                    navigate("/");
                                } else {
                                    navigate(
                                        `/?filter=${encodeURIComponent(value)}`
                                    );
                                }
                            }}
                        />
                    )}
                </Stack>
            )}
            <Group
                align="stretch"
                wrap={isMobile ? "wrap" : "nowrap"}
                w="100%"
                className={classes.layout}
            >
                {!isMobile && (
                    <Stack
                        miw={200}
                        maw={250}
                        gap={0}
                        className={classes.sidebar}
                        w="100%"
                    >
                        <UnstyledButton
                            component={Link}
                            to="/"
                            className={classes.navItem}
                            data-active={isAllAreasActive}
                        >
                            <IconHome className={classes.navItemIcon} />
                            {t("Widget.internal.portal.allAreas")}
                        </UnstyledButton>

                        {relevantGroupings.map((g) => {
                            const isGroupingActive =
                                activeView === "portals" &&
                                selectedGroupingName === g.name;

                            return (
                                <UnstyledButton
                                    key={g.id}
                                    component={Link}
                                    to={
                                        isGroupingActive
                                            ? "/"
                                            : `/?filter=${encodeURIComponent(g.name ?? "")}`
                                    }
                                    className={classes.navItem}
                                    data-active={isGroupingActive}
                                >
                                    {g.name}
                                    <span className={classes.chevron}>
                                        {isGroupingActive ? (
                                            <IconX />
                                        ) : (
                                            <IconChevronRight />
                                        )}
                                    </span>
                                </UnstyledButton>
                            );
                        })}

                        <Box mt="xl" />

                        <UnstyledButton
                            component={Link}
                            to="/my-work"
                            className={classes.navItem}
                            data-active={activeView === "myWork"}
                        >
                            <IconEdit className={classes.navItemIcon} />
                            {t("Widget.internal.portal.myWork")}
                            <span className={classes.chevron}>
                                <IconChevronRight />
                            </span>
                        </UnstyledButton>
                        <UnstyledButton
                            component={Link}
                            to="/history"
                            className={classes.navItem}
                            data-active={activeView === "history"}
                        >
                            <IconHistory className={classes.navItemIcon} />
                            {t("Widget.internal.portal.history")}
                            <span className={classes.chevron}>
                                <IconChevronRight />
                            </span>
                        </UnstyledButton>
                        <UnstyledButton
                            component={Link}
                            to="/chat-search"
                            className={classes.navItem}
                            data-active={activeView === "chatSearch"}
                        >
                            <IconMessage2 className={classes.navItemIcon} />
                            {t("common.recentThreads")}
                            <span className={classes.chevron}>
                                <IconChevronRight />
                            </span>
                        </UnstyledButton>
                    </Stack>
                )}
                <ScrollArea
                    w={"100%"}
                    type="hover"
                    scrollHideDelay={500}
                    scrollbarSize={6}
                    className={classes.contentArea}
                >
                    <Outlet />
                </ScrollArea>
            </Group>
        </Stack>
    );
}
